import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_PLAN_I18N_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductPlanI18nFormModel } from "./type";
import { planI18nInputZodSchema, type PlanI18nInputZodSchemaType } from "./validation-schema";
import { useSendCustomMailSettingForCheckInBefore_00days } from "../../../_shared/model/repository/mutation/check-in-before-00-days";
import { useSendCustomMailSettingForCheckInBefore_01days } from "../../../_shared/model/repository/mutation/check-in-before-01-days";
import { useSendCustomMailSettingForCheckInBefore_07days } from "../../../_shared/model/repository/mutation/check-in-before-07-days";
import { useSendCustomMailSettingForCheckOutAfter_01days } from "../../../_shared/model/repository/mutation/check-out-after-01-days";
import { useSendCustomMailSettingForReservationCancel } from "../../../_shared/model/repository/mutation/reservation-cancel";
import { useSendCustomMailSettingForReservationCreate } from "../../../_shared/model/repository/mutation/reservation-create";
import { useSendCustomMailSettingForReservationUpdate } from "../../../_shared/model/repository/mutation/reservation-update";

import type { ProductPlanI18nFormModelContextType } from "../ui/_shared/context/context";

type Props = {
	inputModel: ProductPlanI18nFormModel["InputModel"];
	queryData: ProductPlanI18nFormModel["QueryData"];
};

export const useInitProductPlanI18nFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<ProductPlanI18nFormModelContextType, "queryData"> & {
	onResetViewModel: () => void;
} => {
	const hotelID = _inputModel.hotelID;
	const planID = _inputModel.planID;
	const lang = _inputModel.lang;

	const defaultInputModel = DEFAULT_PRODUCT_PLAN_I18N_INPUT_MODEL({ planID, hotelID, lang, queryData });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductPlanI18nFormModel["InputModel"],
			ExcludeKeys,
			ProductPlanI18nFormModel["QueryData"],
			PlanI18nInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: planI18nInputZodSchema,
		}),
	);

	const onSetViewModel: ProductPlanI18nFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductPlanI18nFormModel["ViewModel"],
				),
			} satisfies ProductPlanI18nFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductPlanI18nFormModel["InputModel"],
				ExcludeKeys,
				ProductPlanI18nFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductPlanI18nFormModel["InputModel"],
				ExcludeKeys,
				ProductPlanI18nFormModel["QueryData"],
				PlanI18nInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: planI18nInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const onResetViewModel = useCallback(() => {
		setViewModel({
			...viewModel,
			isDirty: false,
		});
	}, [viewModel]);

	const { sendTestMailAtReservationCreate } = useSendCustomMailSettingForReservationCreate();
	const { sendTestMailAtReservationUpdate } = useSendCustomMailSettingForReservationUpdate();
	const { sendTestMailAtReservationCancel } = useSendCustomMailSettingForReservationCancel();
	const { sendTestMailAtCheckInBefore07Days } = useSendCustomMailSettingForCheckInBefore_07days();
	const { sendTestMailAtCheckInBefore01Days } = useSendCustomMailSettingForCheckInBefore_01days();
	const { sendTestMailAtCheckInBefore00Days } = useSendCustomMailSettingForCheckInBefore_00days();
	const { sendTestMailAtCheckOutAfter01Days } = useSendCustomMailSettingForCheckOutAfter_01days();

	return {
		viewModel,
		onSetViewModel,
		onResetViewModel,

		testEmailSender: {
			sendTestMailAtReservationCreate,
			sendTestMailAtReservationUpdate,
			sendTestMailAtReservationCancel,
			sendTestMailAtCheckInBefore07Days,
			sendTestMailAtCheckInBefore01Days,
			sendTestMailAtCheckInBefore00Days,
			sendTestMailAtCheckOutAfter01Days,
		},
	};
};
